/**
 * @fileoverview AI Insights Chat
 * @description Chat interface for asking natural language questions about telemetry.
 *              Generates KQL, executes it, and shows both plain English and technical results.
 */

import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  CircularProgress,
  Chip,
  Collapse,
  Alert,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
} from '@mui/material';
import { Send, Bot, User, Code, ChevronDown, ChevronUp } from 'lucide-react';
import { appInsightsApi } from '../services/appInsightsApi';

const SUGGESTED_QUESTIONS = [
  'Which module is most popular this week?',
  'How many unique users visited the portal in the last 7 days?',
  'What are the slowest API calls?',
  'Which browser do most users use?',
  'Show me failed API calls in the last 24 hours',
  'Which cities are our users in?',
  'How has traffic changed over the last 30 days?',
];

const MessageBubble = ({ message }) => {
  const [showKql, setShowKql] = useState(false);
  const [showData, setShowData] = useState(false);
  const isUser = message.role === 'user';

  return (
    <Box
      sx={{
        display: 'flex',
        gap: 1.5,
        mb: 2,
        flexDirection: isUser ? 'row-reverse' : 'row',
      }}
    >
      {/* Avatar */}
      <Box
        sx={{
          width: 32,
          height: 32,
          borderRadius: '50%',
          bgcolor: isUser ? '#01426A' : '#0097A9',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        {isUser ? <User size={16} color="white" /> : <Bot size={16} color="white" />}
      </Box>

      {/* Content */}
      <Box sx={{ maxWidth: '80%' }}>
        <Paper
          elevation={0}
          sx={{
            p: 1.5,
            bgcolor: isUser ? '#01426A' : '#f5f5f5',
            color: isUser ? 'white' : 'inherit',
            borderRadius: 2,
            borderTopRightRadius: isUser ? 0 : 2,
            borderTopLeftRadius: isUser ? 2 : 0,
          }}
        >
          <Typography variant="body2" sx={{ whiteSpace: 'pre-wrap' }}>
            {message.content}
          </Typography>
        </Paper>

        {/* AI response extras */}
        {!isUser && message.summary && (
          <Paper
            elevation={0}
            sx={{ p: 1.5, mt: 0.5, bgcolor: '#e8f5e9', borderRadius: 2 }}
          >
            <Typography variant="body2" sx={{ fontWeight: 500, color: '#2e7d32' }}>
              {message.summary}
            </Typography>
          </Paper>
        )}

        {/* KQL toggle */}
        {!isUser && message.kql && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              icon={<Code size={12} />}
              label={showKql ? 'Hide KQL' : 'Show KQL'}
              size="small"
              variant="outlined"
              onClick={() => setShowKql(!showKql)}
              deleteIcon={showKql ? <ChevronUp size={12} /> : <ChevronDown size={12} />}
              onDelete={() => setShowKql(!showKql)}
              sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
            />
            <Collapse in={showKql}>
              <Paper sx={{ p: 1, mt: 0.5, bgcolor: '#263238', borderRadius: 1 }}>
                <Typography
                  variant="caption"
                  sx={{ fontFamily: 'monospace', color: '#e0e0e0', whiteSpace: 'pre-wrap', fontSize: '0.7rem' }}
                >
                  {message.kql}
                </Typography>
              </Paper>
              {message.explanation && (
                <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block' }}>
                  {message.explanation}
                </Typography>
              )}
            </Collapse>
          </Box>
        )}

        {/* Data table toggle */}
        {!isUser && message.data?.rows?.length > 0 && (
          <Box sx={{ mt: 0.5 }}>
            <Chip
              label={showData ? 'Hide data' : `Show data (${message.data.row_count} rows)`}
              size="small"
              variant="outlined"
              onClick={() => setShowData(!showData)}
              sx={{ fontSize: '0.7rem', cursor: 'pointer' }}
            />
            <Collapse in={showData}>
              <TableContainer sx={{ mt: 0.5, maxHeight: 250, borderRadius: 1, border: '1px solid #e0e0e0' }}>
                <Table size="small" stickyHeader>
                  <TableHead>
                    <TableRow>
                      {message.data.columns?.map((col) => (
                        <TableCell key={col.name} sx={{ fontWeight: 700, fontSize: '0.65rem', whiteSpace: 'nowrap', py: 0.5 }}>
                          {col.name}
                        </TableCell>
                      ))}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {message.data.rows.slice(0, 50).map((row, idx) => (
                      <TableRow key={idx} hover>
                        {message.data.columns?.map((col) => (
                          <TableCell key={col.name} sx={{ fontSize: '0.65rem', whiteSpace: 'nowrap', py: 0.3 }}>
                            {row[col.name] != null ? String(row[col.name]).substring(0, 60) : '—'}
                          </TableCell>
                        ))}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </TableContainer>
            </Collapse>
          </Box>
        )}
      </Box>
    </Box>
  );
};

const AIInsightsChat = () => {
  const [messages, setMessages] = useState([
    {
      role: 'assistant',
      content: 'Hi! I can answer questions about your portal\'s telemetry data. Ask me anything about user activity, performance, errors, or usage patterns.',
    },
  ]);
  const [input, setInput] = useState('');
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [followUps, setFollowUps] = useState(SUGGESTED_QUESTIONS);
  const messagesEndRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const handleSend = async (question) => {
    const q = (question || input).trim();
    if (!q || loading) return;

    setInput('');
    setError(null);

    // Add user message
    setMessages((prev) => [...prev, { role: 'user', content: q }]);
    setLoading(true);

    try {
      // Send recent history for context (last 6 messages)
      const history = messages.slice(-6).map((m) => ({
        role: m.role === 'assistant' ? 'assistant' : 'user',
        content: m.content,
      }));
      const resp = await appInsightsApi.aiChat(q, history);
      const result = resp?.data || resp;

      setMessages((prev) => [
        ...prev,
        {
          role: 'assistant',
          content: result.summary || 'Here are the results:',
          summary: null,
          kql: result.kql,
          explanation: result.explanation,
          data: result.data,
        },
      ]);

      // Update follow-up suggestions
      if (result.follow_ups?.length > 0) {
        setFollowUps(result.follow_ups);
      }
    } catch (err) {
      let errMsg = 'Failed to get response';
      if (typeof err === 'string') errMsg = err;
      else if (err?.message && typeof err.message === 'string') errMsg = err.message;
      else if (err?.error && typeof err.error === 'string') errMsg = err.error;
      else if (err?.data?.message) errMsg = err.data.message;
      else try { errMsg = JSON.stringify(err); } catch { /* ignore */ }
      
      setError(errMsg);
      setMessages((prev) => [
        ...prev,
        { role: 'assistant', content: `Sorry, I encountered an error: ${errMsg}` },
      ]);
    } finally {
      setLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: 'calc(100vh - 280px)', minHeight: 500 }}>
      {/* Chat messages */}
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          p: 2,
          bgcolor: 'white',
          border: '1px solid',
          borderColor: 'divider',
          borderRadius: 2,
          mb: 2,
        }}
      >
        {messages.map((msg, idx) => (
          <MessageBubble key={idx} message={msg} />
        ))}
        {loading && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center', mb: 2 }}>
            <Box sx={{ width: 32, height: 32, borderRadius: '50%', bgcolor: '#0097A9', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
              <Bot size={16} color="white" />
            </Box>
            <Paper elevation={0} sx={{ p: 1.5, bgcolor: '#f5f5f5', borderRadius: 2, display: 'flex', alignItems: 'center', gap: 1 }}>
              <CircularProgress size={14} />
              <Typography variant="body2" color="text.secondary">Analyzing...</Typography>
            </Paper>
          </Box>
        )}
        <div ref={messagesEndRef} />
      </Box>

      {/* Suggested follow-up questions */}
      {!loading && followUps.length > 0 && (
        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5, mb: 1.5 }}>
          {followUps.map((q) => (
            <Chip
              key={q}
              label={q}
              size="small"
              variant="outlined"
              onClick={() => handleSend(q)}
              sx={{ fontSize: '0.7rem', cursor: 'pointer', '&:hover': { bgcolor: '#e3f2fd' } }}
            />
          ))}
        </Box>
      )}

      {/* Error */}
      {error && (
        <Alert severity="error" sx={{ mb: 1 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {/* Input */}
      <Box sx={{ display: 'flex', gap: 1 }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask a question about your portal's telemetry..."
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={handleKeyDown}
          disabled={loading}
          sx={{ '& .MuiInputBase-root': { borderRadius: 3 } }}
        />
        <IconButton
          onClick={() => handleSend()}
          disabled={loading || !input.trim()}
          sx={{
            bgcolor: '#01426A',
            color: 'white',
            borderRadius: 3,
            px: 2,
            '&:hover': { bgcolor: '#012d4a' },
            '&:disabled': { bgcolor: '#ccc' },
          }}
        >
          <Send size={18} />
        </IconButton>
      </Box>
    </Box>
  );
};

export default AIInsightsChat;
